"use client";

import { AccountAddress } from "@aptos-labs/ts-sdk";
import { IconConfettiFill } from "@decibeltrade/react-icons";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { css } from "styled-system/css";

import { TARGETED_ANNOUNCEMENT_SEEN_KEY } from "@/constants";
import { useLocalStorage } from "@/hooks/storage";
import { useAuthState } from "@/hooks/use-auth-state";
import { TARGETED_ANNOUNCEMENTS } from "@/lib/targeted-announcements/registry";
import type { TargetedAnnouncement } from "@/lib/targeted-announcements/types";
import { linkStyles } from "@/recipes/link";

import { toast } from "./toast";

export function TargetedAnnouncementsNotification() {
  const { accountAddress } = useAuthState();
  if (!accountAddress) return null;
  return (
    <>
      {TARGETED_ANNOUNCEMENTS.map((entry) => (
        <TargetedAnnouncementToastFirer
          key={entry.id}
          entry={entry}
          accountAddress={accountAddress}
        />
      ))}
    </>
  );
}

function TargetedAnnouncementToastFirer({
  entry,
  accountAddress,
}: {
  entry: TargetedAnnouncement;
  accountAddress: string;
}) {
  const t = useTranslations(`targetedAnnouncements.${entry.i18nKey}`);
  const pathname = usePathname();
  const storageKey = `${TARGETED_ANNOUNCEMENT_SEEN_KEY}-${entry.id}-${accountAddress}`;
  const [hasSeen, setHasSeen, isLoading] = useLocalStorage<boolean>(storageKey, false);

  useEffect(() => {
    if (isLoading || hasSeen) return;

    // Already on the destination — mark seen, skip the redundant toast.
    // Compare exact + child paths so sibling routes like `/points-foo` don't match.
    // This runs before the recipient check so we don't pay for the recipients
    // fetch here; the cost is that non-recipients visiting the destination also
    // get marked seen, which is harmless (they'd never be shown the toast).
    const linkHref = entry.link?.href;
    if (linkHref && (pathname === linkHref || pathname.startsWith(`${linkHref}/`))) {
      setHasSeen(true);
      return;
    }

    let cancelled = false;
    const normalized = AccountAddress.fromString(accountAddress).toString();
    // Recipients JSON is fetched on demand so its ~40KB stays out of the
    // first-load bundle.
    entry
      .loadRecipients()
      .then((recipients) => {
        if (cancelled) return;
        if (!recipients.has(normalized)) return;

        const toastId = `targeted-announcement-${entry.id}`;
        toast({
          id: toastId,
          accountScoped: true,
          variant: "success",
          icon: IconConfettiFill,
          iconClassName: css({ color: "yellow.500" }),
          title: t("title"),
          description: () => (
            <span>
              {t.rich("description", {
                link: (chunks) =>
                  linkHref ? (
                    <Link
                      href={linkHref}
                      className={linkStyles}
                      onClick={() => toast.dismiss(toastId)}
                    >
                      {chunks}
                    </Link>
                  ) : (
                    chunks
                  ),
              })}
            </span>
          ),
          duration: Infinity,
        });
        setHasSeen(true);
      })
      .catch(() => {
        // Recipient list failed to download — skip the toast rather than
        // surfacing an unhandled rejection. Non-critical.
      });

    return () => {
      cancelled = true;
    };
  }, [accountAddress, entry, isLoading, hasSeen, pathname, setHasSeen, t]);

  return null;
}
